import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Lock, Loader2, RefreshCw } from "lucide-react";
import { fetchLeads } from "../lib/api";

export default function Admin() {
  const [key, setKey] = useState(localStorage.getItem("urbina_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (k) => {
    setLoading(true);
    try {
      const res = await fetchLeads(k);
      setLeads(res.data);
      setAuthed(true);
      localStorage.setItem("urbina_admin_key", k);
    } catch {
      toast.error("Invalid admin key.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line
  }, []);

  if (!authed) {
    return (
      <main className="bg-forest min-h-screen flex items-center justify-center px-5" data-testid="admin-login">
        <div className="glass-form p-9 w-full max-w-sm">
          <Lock className="text-terracotta mb-4" size={26} />
          <h1 className="font-display text-3xl text-sand mb-6">Lead Dashboard</h1>
          <input
            data-testid="admin-key-input"
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Admin key"
            className="glass-input w-full px-4 py-3 text-sm mb-4"
            onKeyDown={(e) => e.key === "Enter" && load(key)}
          />
          <button
            data-testid="admin-login-btn"
            onClick={() => load(key)}
            disabled={loading}
            className="w-full bg-terracotta text-white py-3 tracking-wide hover:bg-terracotta-dark transition-colors duration-300 flex items-center justify-center"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Enter"}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-sand min-h-screen pt-28 pb-20 px-5 md:px-10" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="font-display text-4xl text-forest">Leads</h1>
            <p className="text-forest/60 text-sm mt-1">{leads.length} total submissions</p>
          </div>
          <button
            data-testid="admin-refresh"
            onClick={() => load(key)}
            className="flex items-center gap-2 border border-forest/20 text-forest px-5 py-2.5 text-sm hover:bg-forest hover:text-sand transition-colors duration-300"
          >
            <RefreshCw size={15} /> Refresh
          </button>
        </div>

        <div className="space-y-4">
          {leads.length === 0 && <p className="text-forest/50">No leads yet.</p>}
          {leads.map((l) => (
            <div key={l.id} className="bg-white border border-forest/10 p-6" data-testid="admin-lead-row">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h3 className="font-display text-2xl text-forest">{l.name}</h3>
                <span className="bg-forest text-sand text-xs px-3 py-1 tracking-wide">{l.intent}</span>
              </div>
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm text-forest/75">
                <p><span className="text-forest/40">Email:</span> {l.email}</p>
                <p><span className="text-forest/40">Phone:</span> {l.phone}</p>
                <p><span className="text-forest/40">State:</span> {l.state || "—"}</p>
                <p><span className="text-forest/40">Budget:</span> {l.budget || "—"}</p>
                <p><span className="text-forest/40">Timeline:</span> {l.timeline || "—"}</p>
              </div>
              {l.message && <p className="mt-3 text-forest/70 text-sm border-t border-forest/10 pt-3">{l.message}</p>}
              <p className="text-forest/35 text-xs mt-3">{new Date(l.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}
